import React from 'react';
import LightBulb from './components/light_bulb';
import {
    CenteredFixedContainer,
    FixedContainer,
} from './style';
import Code from './components/code';
import AppDesign from './components/app_design/component';
import { lerpNum } from '../domain/utils';
import Hands from './components/hands';
import PhoneApp, { calcInsideBorder, calcOutsideBorder } from './components/phone';

import legumoImg from '../../assets/apps/legumo.png';
import doImg from '../../assets/apps/do.png';
import { ReactComponent as AppStore } from '../../assets/icons/appstore.svg';
import { ReactComponent as PlayStore } from '../../assets/icons/playstore.svg';
import { ReactComponent as PlayVideo } from '../../assets/icons/video.svg';
import appConstants from '../domain/config';

// AUXILIARY

interface AppUrl {
    playStore: string,
    appStore: string,
    video: string,
}
const appIcons = (params: { url: AppUrl }) => {
    const height = '1.4em';
    const width = '1em';

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            gap: '.4em',
            marginBottom: '.25em',
        }}
        >
            <a href={params.url.playStore} target="_blank" rel="noreferrer">
                <PlayStore height={height} width={width} />
            </a>
            <a href={params.url.appStore} target="_blank" rel="noreferrer">
                <AppStore height={height} width={width} />
            </a>
            <span style={{ color: 'white', fontSize: 18, fontWeight: 'lighter' }}> | </span>
            <a href={params.url.video} target="_blank" rel="noreferrer">
                <PlayVideo height={height} width={width} />
            </a>
        </div>
    );
};

// MAIN

const calcPerc = (curPerc: number, delay: number, duration: number) => {
    if (curPerc < delay) return 0;
    return Math.min((curPerc - delay) / duration, 1);
};

interface CalcDefinition {
    delayDuration: number;
    unhideDuration: number;
    activeDuration: number;
    hideDuration: number;
}
interface CalcResult {
    loadingPerc: number;
    opacity: number;
    sizeIncreasePerc: number;
}
const calc = (current: number, definition: CalcDefinition): CalcResult => {
    if (current < definition.delayDuration) {
        return { loadingPerc: 0, opacity: 0, sizeIncreasePerc: 0 };
    }
    // eslint-disable-next-line no-param-reassign
    current -= definition.delayDuration;

    if (current < definition.unhideDuration) {
        return {
            opacity: current / definition.unhideDuration,
            loadingPerc: 0,
            sizeIncreasePerc: 0,
        };
    }
    // eslint-disable-next-line no-param-reassign
    current -= definition.unhideDuration;

    if (current < definition.activeDuration) {
        return {
            loadingPerc: current / definition.activeDuration,
            opacity: 1,
            sizeIncreasePerc: 0,
        };
    }
    // eslint-disable-next-line no-param-reassign
    current -= definition.activeDuration;

    if (current < definition.hideDuration) {
        const perc = current / definition.hideDuration;
        return {
            loadingPerc: 1,
            opacity: 1 - perc,
            sizeIncreasePerc: perc,
        };
    }

    return {
        loadingPerc: 1,
        opacity: (definition.unhideDuration < 0) ? 1 : 0,
        sizeIncreasePerc: 1,
    };
};

interface Props {
    perc: number;
}

const Intro: React.FC<Props> = ({ perc }) => {
    const offsetY = 3300 * perc;

    const bulb = calc(offsetY, {
        delayDuration: 0,
        unhideDuration: 0,
        activeDuration: 1000,
        hideDuration: 100,
    });
    const code = calc(offsetY, {
        delayDuration: 1120,
        unhideDuration: 50,
        activeDuration: 700,
        hideDuration: 150,
    });
    const app = calc(offsetY, {
        delayDuration: 2000,
        unhideDuration: 100,
        activeDuration: 600, // change aspectRatio with phone + add hand
        hideDuration: 350,
    });
    const sideAppOpacity = calcPerc(offsetY, 3100, 100);

    const lightPerc = calcPerc(bulb.loadingPerc, 0.25, 0.55);
    const appOpacity = Math.max(app.opacity, app.loadingPerc);

    const percChangeAspectRatio = calcPerc(app.loadingPerc, 0.40, 0.15);
    const percPhone = calcPerc(app.loadingPerc, 0.55, 0.15);
    const percPhonePadding = calcPerc(app.loadingPerc, 0.70, 0.15);
    const percHand = calcPerc(app.loadingPerc, 0.85, 0.15);

    const screenWidth = window.outerWidth;
    const screenHeight = window.innerHeight;

    // PHONE
    const aspectRatio = 765 / 1544;
    const phoneHorizontalPadding = 30 * percPhonePadding;
    const correctedPhoneWidth = Math.min(
        screenHeight * aspectRatio,
        screenWidth,
    ) - phoneHorizontalPadding;
    const correctedPhoneHeight = correctedPhoneWidth / aspectRatio;
    // max
    const maxPhoneWidth = lerpNum(screenWidth, correctedPhoneWidth, percChangeAspectRatio);
    const maxPhoneHeight = lerpNum(screenHeight, correctedPhoneHeight, percChangeAspectRatio);
    // completed
    const phoneWidth = Math.min(700, maxPhoneWidth * (0.3 + 0.7 * (1 - app.sizeIncreasePerc)));
    const phoneHeight = maxPhoneHeight * (0.3 + 0.7 * (1 - app.sizeIncreasePerc));
    // margin
    const appMarginLeft = (screenWidth - phoneWidth) / 2;
    const appMarginBottom = Math.min(screenHeight * 0.03, (screenHeight - phoneHeight) / 2);
    const appMarginTop = screenHeight - phoneHeight - appMarginBottom;

    // hand
    const handHeight = 0.6 * screenHeight * (0.3 + 0.8 * (1 - app.sizeIncreasePerc));
    const leftHandPadding = appMarginLeft - handHeight * 0.13;

    const screenSize = Math.min(screenWidth, screenHeight * 0.4);

    // constant
    const sideApps = {
        sizePerc: 0.8,
        padding: Math.max(screenWidth * 0.05, 12),
    };
    const sideAppsWidth = phoneWidth * sideApps.sizePerc;
    const insideAppWidth = sideAppsWidth - 2 * calcInsideBorder({ width: sideAppsWidth, percPhone })
        - 2 * calcOutsideBorder({ width: sideAppsWidth, percPhone });

    return (
        <>
            {bulb.opacity > 0 && (
                <CenteredFixedContainer style={{ opacity: bulb.opacity }}>
                    <LightBulb
                        lightPerc={lightPerc}
                        size={screenSize * (20 + 20 * bulb.sizeIncreasePerc) / 100}
                    />
                </CenteredFixedContainer>
            )}

            {appOpacity > 0 && (
                <FixedContainer
                    style={{
                        opacity: appOpacity,
                        marginLeft: appMarginLeft,
                        marginTop: appMarginTop,
                    }}
                >
                    <PhoneApp
                        percPhone={percPhone}
                        width={phoneWidth}
                        height={phoneHeight}
                        percMultiplier={1 - app.sizeIncreasePerc}
                    >
                        <div style={{
                            position: 'relative',
                            width: '100%',
                            height: '100%',
                        }}
                        >
                            <div style={{ display: 'absolute', top: 0, left: 0 }}>
                                <AppDesign
                                    percPhone={percPhone}
                                    width={phoneWidth}
                                    height={phoneHeight}
                                    percMultiplier={1 - app.sizeIncreasePerc}
                                />
                            </div>

                            <div style={{
                                opacity: sideAppOpacity,
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                zIndex: 9,
                                background: 'rgba(0, 0, 0, .4)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                            >
                                <div style={{
                                    width: '5em',
                                    height: '5em',
                                    background: '#eee',
                                    color: '#111',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '50%',
                                    fontSize: 16,
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                }}
                                >
                                    <text>Your</text>
                                    <text>App</text>
                                </div>
                            </div>
                        </div>
                    </PhoneApp>
                </FixedContainer>
            )}

            {percHand > 0 && (
                <Hands
                    perc={percHand * 0.7}
                    height={handHeight}
                    offset={leftHandPadding}
                />
            )}

            {code.opacity > 0 && (
                <CenteredFixedContainer style={{ opacity: code.opacity }}>
                    <Code
                        perc={code.loadingPerc}
                        size={screenSize * (40 + 20 * code.sizeIncreasePerc) / 100}
                    />
                </CenteredFixedContainer>
            )}

            {sideAppOpacity > 0 && (
                <FixedContainer
                    style={{
                        opacity: sideAppOpacity,
                        marginLeft: appMarginLeft - phoneWidth - sideApps.padding,
                        marginTop: appMarginTop + phoneHeight * (1 - sideApps.sizePerc) - 28,
                        width: sideAppsWidth * 0.95,
                    }}
                >
                    {appIcons({ url: appConstants.url.apps.do })}
                    <a href={appConstants.url.apps.do.video} target="_blank" rel="noreferrer">
                        <PhoneApp
                            percPhone={percPhone}
                            width={sideAppsWidth}
                            height={phoneHeight * sideApps.sizePerc}
                            percMultiplier={1 - app.sizeIncreasePerc}
                        >
                            <img src={doImg} alt="Do. - Eventos Esportivos" width={insideAppWidth} />
                        </PhoneApp>
                    </a>
                </FixedContainer>
            )}

            {sideAppOpacity > 0 && (
                <FixedContainer
                    style={{
                        opacity: sideAppOpacity,
                        // eslint-disable-next-line max-len
                        marginLeft: appMarginLeft + phoneWidth + phoneWidth * (1 - sideApps.sizePerc) + sideApps.padding,
                        marginTop: appMarginTop + phoneHeight * (1 - sideApps.sizePerc) - 28,
                        width: sideAppsWidth * 0.95,
                    }}
                >
                    {appIcons({ url: appConstants.url.apps.legumo })}
                    <a href={appConstants.url.apps.legumo.video} target="_blank" rel="noreferrer">
                        <PhoneApp
                            percPhone={percPhone}
                            width={sideAppsWidth}
                            height={phoneHeight * sideApps.sizePerc}
                            percMultiplier={1 - app.sizeIncreasePerc}
                        >
                            <img src={legumoImg} alt="Legumo" width={insideAppWidth} />
                        </PhoneApp>
                    </a>
                </FixedContainer>
            )}
        </>
    );
};

export default Intro;
